import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart, Store, ClipboardList, Grid3x3, Ticket, Car, Bike, QrCode,
  Package, Users, BarChart3, Settings, AlertTriangle, Info, Clock, X,
} from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { PageHeader, PlanStatusBadge } from "@/components/common";
import { TourButton } from "@/components/tour/TourButton";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canSee, PAY_LABELS, fmtDateTime, fmtDate } from "@/lib/api";
import { cn } from "@/lib/utils";

const SHORTCUTS = [
  ["Nova venda", "/app/pdv", ShoppingCart, "nova-venda", "pdv"], ["Venda balcão", "/app/pdv", Store, "venda-balcao", "pdv"],
  ["Nova comanda", "/app/comandas?nova=1", ClipboardList, "nova-comanda", "comandas"], ["Mesas", "/app/mesas", Grid3x3, "mesas", "mesas"],
  ["Encomendas", "/app/encomendas", ClipboardList, "encomendas", "encomendas"], ["Tickets", "/app/tickets", Ticket, "tickets", "tickets"],
  ["Estacionamento", "/app/estacionamento", Car, "estacionamento", "estacionamento"],
  ["Delivery", "/app/delivery", Bike, "delivery", "delivery"], ["Cardápio digital", "/app/cardapio", QrCode, "cardapio-digital", "cardapio"],
  ["Produtos", "/app/produtos", Package, "produtos", "produtos"], ["Clientes", "/app/clientes", Users, "clientes", "clientes"],
  ["Relatórios", "/app/relatorios", BarChart3, "relatorios", "relatorios"], ["Configurações", "/app/configuracoes", Settings, "configuracoes", "configuracoes"],
];
const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function DashboardAlerts({ alerts }) {
  const [closed, setClosed] = useState([]);
  const visible = (alerts || []).map((a, i) => ({ ...a, i })).filter((a) => !closed.includes(a.i));
  if (!visible.length) return null;
  return (
    <div className="space-y-2" data-testid="dashboard-alerts">
      {visible.map((a) => (
        <div key={a.i} data-testid={`dashboard-alert-${a.i}`} className={cn("flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm",
          a.level === "danger" ? "border-red-500/40 bg-red-500/10 text-red-500" : a.level === "warning" ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" : "bg-card text-muted-foreground")}>
          {a.level === "info" ? <Info className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
          <span className="flex-1">{a.message}</span>
          <button type="button" onClick={() => setClosed((c) => [...c, a.i])} aria-label="Fechar aviso" data-testid={`dashboard-alert-close-${a.i}`}
            className="rounded-md p-1 opacity-70 hover:opacity-100 hover:bg-foreground/10 transition-opacity"><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

function SubscriptionCard({ sub }) {
  const pct = sub.status === "trial" ? Math.max(4, (sub.days_left / sub.trial_days) * 100) : 100;
  return (
    <div className="card-surface p-5 space-y-3" data-testid="subscription-card">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Assinatura</p>
        <PlanStatusBadge status={sub.status} testId="dashboard-plan-status" />
      </div>
      {sub.status === "trial" ? (
        <>
          <p className="font-display text-3xl font-bold" data-testid="trial-days-left">{sub.days_left} <span className="text-base font-medium text-muted-foreground">dias restantes</span></p>
          <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
          <p className="text-xs text-muted-foreground">Início {fmtDate(sub.trial_start)} · Término {fmtDate(sub.trial_end)}</p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{sub.status === "active" ? `Plano ${sub.plan} ativo.` : sub.alert}</p>
      )}
      {sub.status !== "active" && <Link to="/app/planos" className="text-sm font-semibold text-primary hover:underline" data-testid="dashboard-choose-plan-link">Escolher plano →</Link>}
    </div>
  );
}

function Counter({ label, value, to, icon: Icon, testId }) {
  return (
    <Link to={to} className="card-surface card-hover flex items-center gap-3 p-4" data-testid={testId}>
      <Icon className="h-5 w-5 text-muted-foreground" />
      <div className="flex-1 text-sm text-muted-foreground">{label}</div>
      <span className="font-display text-2xl font-bold">{value}</span>
    </Link>
  );
}

export default function Dashboard() {
  const { session } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard?period=today").then((r) => setD(r.data));
  }, []);
  const prev = session.user.previous_login;

  return (
    <>
      <PageHeader
        eyebrow={new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
        title={`Olá, ${session.user.name.split(" ")[0]}`}
        subtitle={prev?.at ? `Último acesso: ${fmtDateTime(prev.at)} · ${prev.device}` : "Este é o seu primeiro acesso. Bem-vindo!"}
        actions={<TourButton testId="dashboard-tour-btn" />}
      />

      <DashboardAlerts alerts={d?.alerts} />

      <section>
        <p className="eyebrow mb-3">Acesso rápido</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {SHORTCUTS.filter((s) => canSee(session.user, s[4])).map(([label, to, Icon, key]) => (
            <Link key={key} to={to} data-testid={`shortcut-${key}`}
              className="card-surface card-hover flex flex-col items-center justify-center gap-2 p-4 text-center min-h-[92px]">
              <Icon className="h-6 w-6 text-primary" />
              <span className="text-xs sm:text-sm font-medium leading-tight">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-surface p-5 lg:col-span-2" data-testid="sales-timeline-chart">
          <p className="eyebrow mb-4">Vendas por período</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.timeline || []}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4} /><stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={48} />
                <Tooltip formatter={(v) => brl(v)} />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <SubscriptionCard sub={d?.subscription || session.subscription} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-surface p-5" data-testid="payment-methods-chart">
          <p className="eyebrow mb-4">Por forma de pagamento</p>
          {d?.by_method?.length ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.by_method.map((m) => ({ ...m, label: PAY_LABELS[m.name] }))} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => brl(v)} cursor={{ fill: "hsl(var(--accent))" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>{d.by_method.map((_, i) => <Cell key={i} fill={COLORS[i % 5]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">Sem vendas no período.</p>}
        </div>
        <div className="card-surface p-5" data-testid="top-products-list">
          <p className="eyebrow mb-4">Mais vendidos</p>
          <ul className="space-y-3">
            {(d?.top_products || []).slice(0, 6).map((p, i) => (
              <li key={p.name} className="flex items-center gap-3 text-sm">
                <span className="font-mono text-xs text-muted-foreground w-5">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 truncate">{p.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{p.qty}x</span>
                <span className="font-mono font-semibold">{brl(p.total)}</span>
              </li>
            ))}
            {!d?.top_products?.length && <p className="text-sm text-muted-foreground py-10 text-center">Sem vendas no período.</p>}
          </ul>
        </div>
        <div className="grid gap-3" data-testid="operational-counters">
          {canSee(session.user, "delivery") && <Counter label="Pedidos em aberto" value={d?.open_orders ?? "—"} to="/app/delivery" icon={Bike} testId="counter-open-orders" />}
          {canSee(session.user, "mesas") && <Counter label={`Mesas ocupadas${d ? ` de ${d.total_tables}` : ""}`} value={d?.occupied_tables ?? "—"} to="/app/mesas" icon={Grid3x3} testId="counter-occupied-tables" />}
          {canSee(session.user, "comandas") && <Counter label="Comandas abertas" value={d?.open_comandas ?? "—"} to="/app/comandas" icon={ClipboardList} testId="counter-open-comandas" />}
          {canSee(session.user, "tickets") && <Counter label="Tickets pendentes" value={d?.pending_tickets ?? "—"} to="/app/tickets" icon={Ticket} testId="counter-pending-tickets" />}
          {canSee(session.user, "estacionamento") && <Counter label="Veículos / reservas" value={d ? `${d.parked_vehicles} / ${d.parking_reservations}` : "—"} to="/app/estacionamento" icon={Car} testId="counter-parking" />}
        </div>
      </div>
      <p className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-3 w-3" />Acesso atual: {fmtDateTime(session.user.last_login_at)} · {session.user.last_login_device}</p>
    </>
  );
}
